import { createFileRoute } from "@tanstack/react-router";
import { m } from "framer-motion";
import type { ReactNode } from "react";
import { Button, Skeleton } from "@rumbledirect/ui-v2";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { staggerContainer, useMotionInitial } from "@rumbledirect/ui-v2/motion";
import { NewsArticleCard } from "@/news/NewsArticleCard";
import { NewsArticleDetail } from "@/news/NewsArticleDetail";
import type { NewsArticle } from "@/news/newsTypes";
import { useVisibleNews } from "@/news/useVisibleNews";

/** Distinct from the list's `admin_news_article_read` — see the backend facts. */
const READ_DETAILS_PERMISSION = "admin_news_article_read_details";

const SKELETON_CARD_KEYS = Array.from(
  { length: 6 },
  (_, index) => `news-skeleton-${index}`,
);

interface NieuwsSearch {
  /** UUID of the article whose detail dialog is open; undefined when closed. */
  readonly article?: string;
}

export const Route = createFileRoute("/_authenticated/nieuws")({
  // The open article lives in the URL so the detail dialog is shareable and
  // survives reloads/back-forward, matching the pattern in tasks.tsx/dossiers.tsx.
  validateSearch: (search: Record<string, unknown>): NieuwsSearch => ({
    article: typeof search.article === "string" ? search.article : undefined,
  }),
  component: NieuwsPage,
});

function NieuwsPage(): ReactNode {
  const { data, isPending, isError, refetch } = useVisibleNews();
  const currentUser = useCurrentUser();
  const navigate = Route.useNavigate();
  const { article: openArticleId } = Route.useSearch();

  const articles = data?.member;
  const totalItems = data?.totalItems ?? articles?.length ?? 0;
  const canReadDetails = hasPermission(currentUser, READ_DETAILS_PERMISSION);

  const openArticle = (id: string): void => {
    void navigate({ search: (prev) => ({ ...prev, article: id }) });
  };

  const closeArticle = (): void => {
    void navigate({ search: (prev) => ({ ...prev, article: undefined }) });
  };

  return (
    <>
      {/* Visually hidden; the AppShell topbar already shows the title visually. */}
      <h1 className="sr-only">Nieuws</h1>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Aankondigingen en updates van het team.
        </p>
        {!isPending && !isError && (
          <p className="text-sm text-muted-foreground">
            {totalItems} {totalItems === 1 ? "bericht" : "berichten"}
          </p>
        )}
      </div>

      <NewsBody
        isPending={isPending}
        isError={isError}
        articles={articles}
        onOpen={canReadDetails ? openArticle : undefined}
        onRetry={() => {
          void refetch();
        }}
      />

      <NewsArticleDetail
        // Ignore the search param for users without read-details access: the
        // GET has a server-side side effect (it records the view), so we must
        // not fire it just because someone manually navigates to ?article=...
        articleId={canReadDetails ? openArticleId : undefined}
        onOpenChange={(open) => {
          if (!open) closeArticle();
        }}
      />
    </>
  );
}

interface NewsBodyProps {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly articles: readonly NewsArticle[] | undefined;
  readonly onOpen?: (id: string) => void;
  readonly onRetry: () => void;
}

function NewsBody({
  isPending,
  isError,
  articles,
  onOpen,
  onRetry,
}: NewsBodyProps): ReactNode {
  const motionInitial = useMotionInitial("hidden");
  if (isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-surface py-16 text-center shadow-xs"
      >
        <p className="text-muted-foreground">
          We konden het nieuws niet laden.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Opnieuw proberen
        </Button>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {SKELETON_CARD_KEYS.map((key) => (
          <div
            key={key}
            className="overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-xs"
          >
            <Skeleton className="aspect-16/9 w-full rounded-none" />
            <div className="flex flex-col gap-2 p-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (articles === undefined || articles.length === 0) {
    return (
      <div className="rounded-2xl border border-border/70 bg-surface py-16 text-center text-muted-foreground shadow-xs">
        Geen nieuws gevonden.
      </div>
    );
  }

  return (
    <m.div
      variants={staggerContainer}
      initial={motionInitial}
      animate="visible"
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
    >
      {articles.map((article) => (
        <NewsArticleCard key={article.id} article={article} onOpen={onOpen} />
      ))}
    </m.div>
  );
}
